import { useState } from "react";
import "./confirm-pop-up.scss";
import { useDialogo, aoClicarNoFundo } from "./use-dialogo";

export type PopUpInfo = {
  // setAnswer: Dispatch<React.SetStateAction<boolean>>;
  type: "confirm" | "input" | "alert";
  text: string;
  close: () => any;
  confirm: (input: string) => any;
  inputDefault?: string;
  showMathKeyboard?: boolean;
  /**
   * O QUE O BOTÃO DE CONFIRMAR DIZ — `T8.26`.
   *
   * Ele, na letra: *"O botão 'Sim' podia dizer o que faz ('Limpar página', 'Excluir')."* E o pedido é
   * mais sério do que parece num diálogo de apagar: "Sim" responde a uma pergunta que a pessoa pode
   * ter parado de ler. O rótulo que nomeia a ação é a última chance de ela perceber o que vai
   * acontecer — e é o único texto que fica na tela depois de o olho passar pelo título.
   *
   * Sem este campo o botão continua dizendo "Sim", porque trocar o padrão de todos os diálogos de uma
   * vez mudaria telas que ninguém mediu.
   */
  rotuloConfirmar?: string;
  /**
   * O TIPO DO CAMPO — `T8.15`, e ele existe por causa de uma SENHA.
   *
   * O `prompt()` do navegador era usado para pedir a senha atual em duas ações da tela de conta. Além
   * de ser diálogo nativo — o que o achado proíbe —, `prompt()` **mostra o que se digita**: a senha
   * fica na tela, num computador que numa escola é compartilhado.
   *
   * O padrão continua `text`, porque é o que as outras caixas pedem.
   */
  tipoDoCampo?: "text" | "password";
};

const MATH_SYMBOLS = {
  "÷": "\\frac{}{}",
  "√": "\\sqrt{}",
  "∞": "\\infty",
  "∑": "\\sum_{a}^{b}",
  "∫": "\\int_{a}^{b}",
  π: "\\pi",
  "≅": "\\cong",
  "≈": "\\approx",
  "≠": "\\neq",
  "<": "\\lt",
  ">": "\\gt",
  "≤": "\\leq",
  "≥": "\\geq",
};

function isBetweenDollars(value: string, pos: number) {
  if (pos === 0 || pos === value.length || value.length < 3) {
    return false;
  }

  let foundRight = false;
  let foundLeft = false;
  for (let i = pos - 1, j = pos; i > 0 || j < value.length; ) {
    foundLeft = value[i] === "$";
    foundRight = value[j] === "$";

    if (foundLeft && foundRight) return true;

    if (!foundLeft && i > 0) i--;
    if (!foundRight && j < value.length) j++;
  }

  return foundLeft && foundRight;
}

function ConfirmPopUp({
  type,
  text,
  close,
  confirm,
  inputDefault,
  showMathKeyboard,
  rotuloConfirmar,
  tipoDoCampo,
}: PopUpInfo) {
  showMathKeyboard ??= false;

  const [input, setInput] = useState(inputDefault ?? "");

  /**
   * POR QUE O CONFIRMAR EM BRANCO NÃO FEZ NADA — T8.20.
   *
   * Havia um `if (!trimmedInput) return;` aqui, antes de chamar o `confirm` de quem abriu a caixa. O
   * botão aceitava o clique e a tela não mudava: nem fechava, nem explicava, nem seguia.
   *
   * E o pior é que a explicação EXISTIA — o editor tinha "Dê um nome ao seu quadrinho para poder
   * guardá-lo." escrito no `confirm` dele. Era código inalcançável, porque este retorno vinha antes.
   *
   * A guarda fica (mandar corda vazia a quem espera um nome é pior), mas passa a DIZER. E fica aqui e
   * não em cada chamador: são quatro caixas de texto no produto, e a que esqueceria a frase seria
   * justamente a próxima.
   */
  const [porQueNaoDeu, setPorQueNaoDeu] = useState<string | null>(null);

  // T8.5/T8.18/T8.26 · Esc fecha, e a pagina de tras nao rola. Tres achados do relatorio dizem a
  // mesma frase por tres telas: "Esc nao fecha". O porque das tres coisas esta em `use-dialogo.ts`.
  useDialogo(close);

  function MathKeyboard() {
    return (
      // T2.106 · AS QUINZE TECLAS MEDIAM 22x22 px, e a regua deste produto e 44. Medido na
      // varredura do editor, na caixa que pergunta o texto do balao: quinze alvos entre 22 e 24 px
      // num teclado que existe para a crianca escrever formula com o dedo.
      //
      // O estilo saiu de `style` inline para classe porque alvo de toque e regra do produto, e nao
      // decoracao desta caixa: escrita aqui, ela vale para o proximo teclado tambem.
      <div className="teclado-de-simbolos">
        {Object.entries(MATH_SYMBOLS).map(([symbol, latex]) => (
          <button
            key={symbol}
            type="button"
            className="tecla-de-simbolo"
            aria-label={`Inserir ${symbol}`}
            onClick={() => {
              const inputEl = document.getElementById(
                "confirm-pop-up-input"
              ) as HTMLInputElement | null;
              const cursorPos = inputEl?.selectionStart ?? input.length - 1;
              if (!isBetweenDollars(input, cursorPos)) {
                latex = "$" + latex + "$";
              }
              setInput(
                input.slice(0, cursorPos) + latex + input.slice(cursorPos)
              );

              // setInput((prev) => prev + symbol);
            }}
          >
            {symbol}
          </button>
        ))}
      </div>
    );
  }

  return (
    // O clique fora fecha — e e o gesto de quem nunca ouviu falar de Esc, que e a maioria de quem
    // usa este produto. Fechar por fora NUNCA confirma: some sem fazer nada, que e o certo num
    // dialogo cuja resposta apaga coisa.
    <div className="confirm-popup-bg" onClick={aoClicarNoFundo(close)}>
      <div className="confirm-popup-container" role="dialog" aria-modal="true">
        <p>{text}</p>

        {type === "confirm" && (
          <div className="buttons-container">
            <button onClick={close} style={{ borderColor: "orange" }}>
              Cancelar
            </button>
            <button
              onClick={() => confirm(input)}
              style={{ borderColor: "green" }}
            >
              {/* T8.26 · "Sim" responde a uma pergunta que a pessoa pode ter parado de ler. */}
              {rotuloConfirmar ?? "Sim"}
            </button>
          </div>
        )}

        {type === "input" && (
          <>
            <input
              id="confirm-pop-up-input"
              type={tipoDoCampo ?? "text"}
              maxLength={1000}
              autoComplete={tipoDoCampo === "password" ? "current-password" : "off"}
              autoFocus
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                // O aviso sai na primeira letra: deixá-lo enquanto a pessoa já está resolvendo é
                // repetir uma crítica que deixou de valer.
                if (porQueNaoDeu) setPorQueNaoDeu(null);
              }}
            />
            {/* Teclado de matematica num campo de senha nao faz sentido, e mostraria simbolos que a
                pessoa nao esta escrevendo ali. */}
            {/* T8.20 · junto do campo, e não num alerta em cima: o erro é DO campo, e o olho de quem
                acabou de clicar está aqui. `role="alert"` para leitor de tela anunciar sem ir procurar. */}
            {porQueNaoDeu && (
              <p className="confirm-popup-vazio" role="alert">
                {porQueNaoDeu}
              </p>
            )}

            {showMathKeyboard && tipoDoCampo !== "password" && <MathKeyboard />}

            <div className="buttons-container">
              <button onClick={close} style={{ borderColor: "orange" }}>
                Cancelar
              </button>
              <button
                onClick={() => {
                  const trimmedInput = input.trim();
                  setInput(trimmedInput);
                  if (!trimmedInput) {
                    // T8.20 · era `return` seco, e o botão não fazia nada nem explicava.
                    setPorQueNaoDeu(
                      tipoDoCampo === "password"
                        ? "Digite a sua senha para continuar."
                        : "Escreva algo aqui para continuar."
                    );
                    return;
                  }

                  setPorQueNaoDeu(null);
                  confirm(trimmedInput);
                }}
                style={{ borderColor: "green" }}
              >
                Confirmar
              </button>
            </div>
          </>
        )}

        {type === "alert" && (
          <>
            <div
              className="buttons-container"
              style={{ justifyContent: "center" }}
            >
              <button onClick={close} style={{ borderColor: "green" }}>
                Ok
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConfirmPopUp;
